import {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {A4_H, FLOW_GAP} from '../lib/preview-geometry';
import {HEADER_GROUP_ID, getHeaderScope} from '../lib/constants';
import {isPageNumber, resolvePageNumberConfig} from '../lib/page-numbers';
import type {HeaderScope, PhotoRequirement} from '../lib/types';
import {effectivePositionModeIn, sentinelsById} from '../lib/position-mode';

interface UsePreviewLayoutParams {
  requirements: PhotoRequirement[];
  marginT: number;
  marginB: number;
  fillMode: boolean;
}

/**
 * Derives the paginated page layout from the template requirements: the flow /
 * header / absolute item lists, per-group header regions, measured flow-item
 * heights, and the resulting page groups (content split across A4 pages). Pure
 * derivation + a DOM height-measurement pass; no interaction state. Extracted
 * from LivePreview so the component focuses on rendering + interaction.
 */
export function usePreviewLayout({
  requirements,
  marginT,
  marginB,
  fillMode,
}: UsePreviewLayoutParams) {
  const sentinelIds = useMemo(
    () => new Set(requirements.filter((r) => r.type === 'group').map((r) => r.id)),
    [requirements],
  );
  const sentinelMap = useMemo(() => sentinelsById(requirements), [requirements]);

  // ── Effective position mode ───────────────────────────────────────────────────
  // Children of an absolute-mode sentinel group render as absolute regardless of
  // their own stored position_mode — so the whole group moves as one unit.
  // Children of a flow-mode sentinel group render per their own position_mode.
  // The rule itself lives in `effectivePositionModeIn`, shared with the drag
  // surface and the paste, which have to agree with what is drawn here.
  const effectivePositionMode = useCallback(
    (item: PhotoRequirement) => effectivePositionModeIn(item, sentinelMap),
    [sentinelMap],
  );

  // ── Item lists ────────────────────────────────────────────────────────────────
  // Sentinels (type === "group") are excluded from rendering lists — they are
  // invisible metadata elements. effectivePositionMode promotes children of
  // absolute-mode groups to absolute so they move as a unit.
  // A page-number stamp is placed by its own band rule, so it belongs to NEITHER
  // per-page list: it is not content on one page but a mark repeated across a RANGE
  // of pages, and the pagination must neither reserve height for it (it sits in the
  // margin) nor pin it to the page its y happens to fall on.
  //
  // Partitioned once, here, rather than excluded again inside each list below — the
  // two derived lists then cannot fall out of step. Each stamp's config is resolved
  // in the same pass, so `ResolvedPageNumber` is what the render path receives and
  // nothing re-coerces it per page.
  const pageNumberStamps = useMemo(
    () =>
      requirements
        .filter(isPageNumber)
        .map((item) => ({item, pn: resolvePageNumberConfig(item.config)})),
    [requirements],
  );
  const positionedItems = useMemo(
    () => requirements.filter((r) => !isPageNumber(r)),
    [requirements],
  );

  const flowItems = useMemo(
    () =>
      positionedItems
        // filter() already copies, so sorting in place here is safe.
        .filter((r) => effectivePositionMode(r) === 'flow' && r.type !== 'group')
        .sort((a, b) => a.order - b.order),
    [positionedItems, effectivePositionMode],
  );
  // Sentinel groups marked is_header: true form the Header region shown at the page top.
  const headerSentinelIds = useMemo(
    () => new Set(requirements.filter((r) => r.type === 'group' && r.is_header).map((r) => r.id)),
    [requirements],
  );
  // Header items are flow items that belong to the header region (new-style sentinel OR legacy group_id).
  // The header group always behaves as a flow region — absolute positioning within it is not supported.
  const headerItems = useMemo(
    () =>
      flowItems.filter(
        (r) =>
          r.group_id === HEADER_GROUP_ID ||
          (r.group_id != null && headerSentinelIds.has(r.group_id)),
      ),
    [flowItems, headerSentinelIds],
  );
  const bodyFlowItems = useMemo(
    () =>
      flowItems.filter(
        (r) =>
          r.group_id !== HEADER_GROUP_ID &&
          !(r.group_id != null && headerSentinelIds.has(r.group_id)),
      ),
    [flowItems, headerSentinelIds],
  );
  const absoluteItems = useMemo(
    () =>
      positionedItems.filter((r) => effectivePositionMode(r) === 'absolute' && r.type !== 'group'),
    [positionedItems, effectivePositionMode],
  );
  // Absolute items belonging to a header sentinel are rendered within the header zone at
  // header-zone-relative coordinates (translated when the group was marked as header).
  const absoluteHeaderItems = useMemo(
    () => absoluteItems.filter((r) => r.group_id != null && headerSentinelIds.has(r.group_id)),
    [absoluteItems, headerSentinelIds],
  );
  const absoluteHeaderItemIds = useMemo(
    () => new Set(absoluteHeaderItems.map((r) => r.id)),
    [absoluteHeaderItems],
  );

  // ── Height measurement ────────────────────────────────────────────────────────
  const measureRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [measuredHeights, setMeasuredHeights] = useState<Map<string, number>>(new Map());

  // Measure rendered flow-item heights after every render so the pagination logic
  // can split content across pages. This is a legitimate DOM-measurement effect: it
  // intentionally runs on every render (no dep array), and the guard below bails out
  // when nothing changed, so it cannot trigger a render loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const heights = new Map<string, number>();
    measureRefs.current.forEach((el, id) => {
      heights.set(id, el.offsetHeight);
    });
    // Measured DOM heights synced into state; the guard below returns the prior
    // map when nothing changed, so this cannot loop.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMeasuredHeights((prev) => {
      if (heights.size !== prev.size) return heights;
      for (const [id, h] of heights) {
        if (prev.get(id) !== h) return heights;
      }
      return prev;
    });
  });

  const availableH = A4_H - marginT - marginB;

  // ── Header regions (one per header group) ──────────────────────────────────────
  // Each header group displays on the pages covered by its own `header_scope`, so
  // different headers render on different pages independently. Overlapping ranges are
  // out of scope, so at most one header is treated as active per page (first match).
  interface HeaderRegion {
    id: string; // sentinel id, or HEADER_GROUP_ID for legacy headers
    scope: HeaderScope;
    flow: PhotoRequirement[];
    abs: PhotoRequirement[];
    label: string;
    isLegacy: boolean;
  }

  const headerRegions = useMemo<HeaderRegion[]>(() => {
    const regions: HeaderRegion[] = [];
    // New-style sentinel headers, each with its configured scope.
    for (const sentinel of requirements) {
      if (sentinel.type !== 'group' || !sentinel.is_header) continue;
      regions.push({
        id: sentinel.id,
        scope: getHeaderScope(sentinel),
        flow: headerItems.filter((r) => r.group_id === sentinel.id),
        abs: absoluteHeaderItems.filter((r) => r.group_id === sentinel.id),
        label: sentinel.label || 'Header',
        isLegacy: false,
      });
    }
    // Legacy loose header members (pre-sentinel templates) behave as an every-page header.
    const legacyFlow = headerItems.filter((r) => r.group_id === HEADER_GROUP_ID);
    if (legacyFlow.length > 0) {
      regions.push({
        id: HEADER_GROUP_ID,
        scope: {from: 1},
        flow: legacyFlow,
        abs: [],
        label: 'Header',
        isLegacy: true,
      });
    }
    return regions;
  }, [requirements, headerItems, absoluteHeaderItems]);

  // Measured height of a header region: the greater of its stacked flow content and the
  // bottom edge of any absolute header items (which carry header-zone-relative y).
  const regionHeight = useCallback(
    (region: HeaderRegion) => {
      if (region.flow.length === 0 && region.abs.length === 0) return 0;
      const flowH =
        region.flow.reduce((sum, item) => sum + (measuredHeights.get(item.id) ?? 0), 0) +
        FLOW_GAP * Math.max(0, region.flow.length - 1);
      const absBottom = region.abs.reduce(
        (max, r) => Math.max(max, (r.y ?? 0) + (r.height ?? 0)),
        0,
      );
      return Math.max(flowH, absBottom) + FLOW_GAP;
    },
    [measuredHeights],
  );

  // The header region active on a given (0-based) page, by scope. First match wins.
  const headerForPage = useCallback(
    (pageIdx: number): HeaderRegion | undefined => {
      const pageNo = pageIdx + 1;
      return headerRegions.find(
        (r) => pageNo >= r.scope.from && (r.scope.to === undefined || pageNo <= r.scope.to),
      );
    },
    [headerRegions],
  );

  // Body height available on a given page: the active header's height is subtracted;
  // pages with no active header get the full height.
  const availForPage = useCallback(
    (pageIdx: number) => {
      const region = headerForPage(pageIdx);
      return availableH - (region ? regionHeight(region) : 0);
    },
    [availableH, headerForPage, regionHeight],
  );

  interface PageGroup {
    items: PhotoRequirement[];
    pageBreakId?: string;
  }

  const pageGroups = useMemo<PageGroup[]>(() => {
    const groups: PageGroup[] = [{items: []}];
    let usedH = 0;
    for (const item of bodyFlowItems) {
      if (item.type === 'page_break') {
        groups.push({items: [], pageBreakId: item.id});
        usedH = 0;
        continue;
      }
      const h = (measuredHeights.get(item.id) ?? 0) + FLOW_GAP;
      if (usedH > 0 && h > 0 && usedH + h > availForPage(groups.length - 1)) {
        groups.push({items: []});
        usedH = 0;
      }
      groups[groups.length - 1]!.items.push(item);
      usedH += h;
    }
    return groups;
  }, [bodyFlowItems, measuredHeights, availForPage]);

  // Rendered body-flow item ids in visual order (page breaks are not rendered as
  // items). Drives drop hit-testing and the insertion indicator across pages.
  const orderedFlowRenderIds = useMemo(
    () => pageGroups.flatMap((g) => g.items.map((i) => i.id)),
    [pageGroups],
  );

  const absoluteByPage = useMemo(() => {
    const map = new Map<number, PhotoRequirement[]>();
    for (const item of absoluteItems) {
      if (absoluteHeaderItemIds.has(item.id)) continue;
      const idx = Math.max(0, Math.floor((item.y ?? 0) / A4_H));
      const list = map.get(idx) ?? [];
      list.push(item);
      map.set(idx, list);
    }
    // Stacking order follows `order`: later = painted on top (all share the same
    // z-index, so DOM/render order decides). Drives the right-click layering menu.
    for (const list of map.values()) list.sort((a, b) => a.order - b.order);
    return map;
  }, [absoluteItems, absoluteHeaderItemIds]);

  // Absolute-mode sentinels rendered as a draggable bounding-box handle (non-header groups only).
  const absoluteSentinelsByPage = useMemo(() => {
    const map = new Map<number, PhotoRequirement[]>();
    for (const r of requirements) {
      if (r.type !== 'group' || r.position_mode !== 'absolute' || r.is_header) continue;
      const idx = Math.max(0, Math.floor((r.y ?? 0) / A4_H));
      const list = map.get(idx) ?? [];
      list.push(r);
      map.set(idx, list);
    }
    return map;
  }, [requirements]);

  // In edit mode, render enough pages to reach the first page each header is scoped to
  // start on (so a header scoped to e.g. page 3 is visible). We intentionally do NOT
  // force an extra page beyond the content + header start pages — otherwise deleting the
  // last content page would leave a phantom page that only repeats the header and can't
  // be removed.
  const minHeaderPages =
    !fillMode && headerRegions.some((r) => r.flow.length > 0 || r.abs.length > 0)
      ? Math.max(1, ...headerRegions.map((r) => r.scope.from))
      : 1;
  const totalPages = Math.max(
    pageGroups.length,
    minHeaderPages,
    absoluteByPage.size > 0 ? Math.max(...absoluteByPage.keys()) + 1 : 1,
    absoluteSentinelsByPage.size > 0 ? Math.max(...absoluteSentinelsByPage.keys()) + 1 : 1,
  );

  return {
    pageNumberStamps,
    sentinelIds,
    sentinelMap,
    flowItems,
    absoluteItems,
    headerRegions,
    headerForPage,
    regionHeight,
    pageGroups,
    orderedFlowRenderIds,
    absoluteByPage,
    absoluteSentinelsByPage,
    totalPages,
    measureRefs,
  };
}
